import React, { useState, useEffect } from 'react';
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, ChevronsUpDown, Folder, Search } from "lucide-react";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import axios from 'axios';
import Cookies from 'js-cookie';
import { BACKEND_API_URL } from "../utils/api";

const REACT_APP_BACKEND_API_URL = BACKEND_API_URL;

const FolderDropdown = ({ onFolderSelect, folder_id }) => {
    const [folders, setFolders] = useState([]);
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [open, setOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const fetchFolders = async () => {
            try {
                const accessToken = Cookies.get('access_token');
                let url = `${REACT_APP_BACKEND_API_URL}/api/folders_list/`;
                if (folder_id !== null) {
                    url += `?folder_id=${folder_id}`;
                }
                const response = await axios.get(url, {
                    headers: {
                        Authorization: `Token ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });
                setFolders(response.data.folders || []);
            } catch (error) {
                console.error('Error fetching folders:', error);
            }
        };

        fetchFolders();
    }, [folder_id]);

    const handleFolderSelect = (folder) => {
        setSelectedFolder(folder);
        setOpen(false);
        if (onFolderSelect) {
            onFolderSelect(folder.id);
        }
    };

    const filteredFolders = folders.filter(folder =>
        folder.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-2">
            <Label className="text-sm font-medium">Select a Folder</Label>
            <Popover open={open} onOpenChange={setOpen} modal={false}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="w-full justify-between"
                    >
                        <span className="flex items-center gap-2 truncate">
                            {selectedFolder ? (
                                <>
                                    <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                                    <span className="truncate">{selectedFolder.name}</span>
                                </>
                            ) : (
                                <span className="text-muted-foreground">Search Folder...</span>
                            )}
                        </span>
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent 
                    className="w-(--radix-popover-trigger-width) p-0" 
                    align="start"
                    onWheel={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center border-b px-3">
                        <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                        <Input
                            placeholder="Search folders..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="border-0 focus-visible:ring-0 shadow-none"
                        />
                    </div>
                    <div className="max-h-[200px] overflow-y-auto overscroll-contain">
                        {filteredFolders.length === 0 ? (
                            <div className="py-6 text-center text-sm text-muted-foreground">
                                No folders found.
                            </div>
                        ) : (
                            <div className="p-1">
                                {filteredFolders.map((folder) => (
                                    <div
                                        key={folder.id}
                                        onClick={() => handleFolderSelect(folder)}
                                        className={cn(
                                            "flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer",
                                            "hover:bg-accent hover:text-accent-foreground",
                                            selectedFolder?.id === folder.id && "bg-accent"
                                        )}
                                    >
                                        <Check
                                            className={cn(
                                                "h-4 w-4 shrink-0",
                                                selectedFolder?.id === folder.id ? "opacity-100" : "opacity-0"
                                            )}
                                        />
                                        <Folder className="h-4 w-4 shrink-0 text-muted-foreground" />
                                        <span className="truncate">{folder.name}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
};

export default FolderDropdown;
